import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchJoborders,
    createJobOrder,
    updateJobOrder,
    deleteJobOrder,
    setPagination,
    setSort,
    searchJoborders,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    createAdvancePayment,
    resetFiltersAndPagination
} from '../../store/modules/joborder/joborderActions';
import PaymentForm from '../../components/pageModule/Joborders/PaymentForm';
import { jobStatus } from '../../utils/statusUtil';
import { useHasPermission } from '../../utils/permissionUtils';

const MySwal = withReactContent(Swal);

const Joborders = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = [] }) => {

    const hasCreatePermission = useHasPermission(['create job orders']);
    const hasUpdatePermission = useHasPermission(['update job orders']);
    const hasViewPermission = useHasPermission(['view job orders']);

    const joborderState = useSelector(state => state.joborder);
    const dispatch = useDispatch();
    
    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createJobOrder(formData));
            toast.success('Job Order created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating joborder', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateJobOrder(id, formData));
            toast.success('Job Order updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating joborder', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteJobOrder(id));
                toast.success('JobOrder deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting joborder', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handlePayment = async (id, formData) => {

        try {
            const response = await dispatch(createAdvancePayment(id, formData));
            toast.success('Advance Payment added successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error adding advance payment', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    }


    const columns = [
        {
            name: 'Job Order Number',
            selector: row => row.order_number,
            wrap: true,
            minWidth: '180px'
        },
        {
            name: 'Job Date',
            selector: row => row.order_date,
            sortField: 'job_orders.order_date',
            sortable: true,
            wrap: true,
            minWidth: '120px'
        },
        {
            name: 'Name',
            selector: row => row?.customer_name,
            wrap: true,
            minWidth: '200px'
        },
        {
            name: 'Branch Name',
            selector: row => row.branch_name || 'N/A',
            wrap: true,
            minWidth: '150px'
        },
        {
            name: 'Net Amount',
            selector: row => row.net_total,
            wrap: true,
            minWidth: '100px'
        },
        {
            name: 'Adv. Paid',
            selector: row => row.advance_payment,
            wrap: true,
            minWidth: '100px'
        },
        {
            name: 'Status',
            selector: row => jobStatus(row.status_id, row.status),
            sortField: 'job_orders.status',
            sortable: true,
            wrap: true,
            minWidth: '120px'
        },
        {
            name: 'Created At',
            selector: row => row.created_at,
            sortField: 'job_orders.created_at',
            sortable: true,
            wrap: true,
            minWidth: '150px'
        }
    ];

    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchJoborders(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        payment: handlePayment,
        search: (search) => dispatch(searchJoborders(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search', 'filter', 'actions', 'payment'];

    if (hasViewPermission) {
        defaultAvailableActions = ['view', ...defaultAvailableActions];
    }
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['edit', ...defaultAvailableActions];
    }

    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Job Orders"
                moduleName="JobOrder"
                moduleKey="joborders"
                state={joborderState}
                actions={actions}
                SubActionComponent={PaymentForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                onSelectedRowsChange={onSelectedRowsChange}
                urlKey="job_orders"
                actionType="url"

                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Joborders; 